// src/feature-module/settings/facilitymaintaince/Job_Card/ViewJobCardModule.tsx
import React from 'react';
import { Modal, Button, Form, Row, Col, Badge } from 'react-bootstrap';

interface UserDTO {
  id: number;
  empNumber: string;
  name: string;
}

interface JobCardDTO {
  id: number;
  maintenanceTaskId: number | null;
  maintenanceDescription: string | null;
  locationName?: string | null;
  buildingName?: string | null;
  floorName?: string | null;
  roomName?: string | null;
  parentJobCardId: number | null;
  responsiblePersons: UserDTO[];
  accountablePersons: UserDTO[];
  scheduledDateTime: string | null;
  scheduledEndDateTime: string | null;
  status: string;
  comment: string | null;
}

interface Props {
  show: boolean;
  onHide: () => void;
  jobCard: JobCardDTO;
}

const ViewJobCardModule: React.FC<Props> = ({ show, onHide, jobCard }) => {
  const locationParts = [
    jobCard.locationName,
    jobCard.buildingName,
    jobCard.floorName,
    jobCard.roomName,
  ].filter(Boolean);

  const locationPath = locationParts.length > 0 ? locationParts.join(' → ') : 'Not specified';

  const formatDateTime = (isoString: string | null) => {
    if (!isoString) return '—';
    return new Date(isoString).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const getUserLabel = (user: UserDTO) => {
    const emp = user.empNumber?.trim();
    return emp ? `${emp} - ${user.name.trim()}` : user.name.trim();
  };

  const getStatusColor = (status: string) => {
    switch (status.toUpperCase()) {
      case 'PENDING': return 'warning';
      case 'IN_PROGRESS': return 'info';
      case 'COMPLETED': return 'success';
      case 'NOT_COMPLETED': return 'danger';
      case 'CANCELLED': return 'dark';
      case 'EXPIRED': return 'secondary';
      default: return 'primary';
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header className="d-flex justify-content-between align-items-center">
        <Modal.Title>
          Job Card #{jobCard.id}
          {jobCard.parentJobCardId && (
            <small className="text-muted ms-3">
              (Child of Job Card #{jobCard.parentJobCardId})
            </small>
          )}
        </Modal.Title>

        {/* Status Badge - Right side, near close button */}
        <Badge
          bg={getStatusColor(jobCard.status)}
          text="white"
          pill
          className="py-2 px-4 fs-6 me-3"
        >
          {jobCard.status.replace('_', ' ')}
        </Badge>
      </Modal.Header>

      <Modal.Body>
        <Row>
          <Col lg={7}>
            <Form.Group className="mb-4">
              <Form.Label className="fw-bold">Maintenance Task</Form.Label>
              <Form.Control
                readOnly
                value={
                  jobCard.maintenanceTaskId
                    ? `#${jobCard.maintenanceTaskId} - ${jobCard.maintenanceDescription || 'No description'}`
                    : '—'
                }
                className="bg-light border-0 fs-5"
              />
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label className="fw-bold ">Location</Form.Label>
              <Form.Control readOnly value={locationPath} className="bg-light border-0" />
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label className="fw-bold">
                Responsible Persons ({jobCard.responsiblePersons.length})
              </Form.Label>
              <div className="d-flex flex-wrap gap-2 mt-2">
                {jobCard.responsiblePersons.length > 0 ? (
                  jobCard.responsiblePersons.map((user) => (
                    <Badge key={user.id} text="white" pill className="py-2 px-3">
                      <i className="ti ti-user-check me-2" />
                      {getUserLabel(user)}
                    </Badge>
                  ))
                ) : (
                  <span className="text-muted fst-italic">— No responsible persons assigned</span>
                )}
              </div>
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label className="fw-bold ">
                Accountable Persons ({jobCard.accountablePersons.length})
              </Form.Label>
              <div className="d-flex flex-wrap gap-2 mt-2">
                {jobCard.accountablePersons.length > 0 ? (
                  jobCard.accountablePersons.map((user) => (
                    <Badge key={user.id} text="white" pill className="py-2 px-3">
                      <i className="ti ti-user-shield me-2" />
                      {getUserLabel(user)}
                    </Badge>
                  ))
                ) : (
                  <span className="text-muted fst-italic">— No accountable persons assigned</span>
                )}
              </div>
            </Form.Group>
          </Col>

          <Col lg={5}>
            <Form.Group className="mb-4">
              <Form.Label className="fw-bold ">Scheduled Start</Form.Label>
              <Form.Control readOnly value={formatDateTime(jobCard.scheduledDateTime)} className="bg-light border-0" />
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label className="fw-bold ">Scheduled End</Form.Label>
              <Form.Control readOnly value={formatDateTime(jobCard.scheduledEndDateTime)} className="bg-light border-0" />
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label className="fw-bold ">Comment</Form.Label>
              <Form.Control
                as="textarea"
                rows={6}
                readOnly
                value={jobCard.comment?.trim() || '— No comment provided'}
                className="bg-light border-0"
                style={{ resize: 'none' }}
              />
            </Form.Group>
          </Col>
        </Row>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" size="lg" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ViewJobCardModule;